import { motion } from 'framer-motion';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import CredentialsHero from './components/CredentialsHero';
import CertificationTimeline from './components/CertificationTimeline';
import TrustBadges from './components/TrustBadges';
import CoverageMap from './components/CoverageMap';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://lonetrs.com';

const proofSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: 'Verified Credentials &amp; Licenses | LONE TRS LLC',
  description:
    'LONE TRS LLC is fully licensed, insured, and DOT/MC certified. View our DOT #4476843, MC #1767623, Nevada Business License NV20253377515, and full certification timeline.',
  url: `${SITE_URL}/proof`,
  isPartOf: { '@type': 'WebSite', name: 'LONE TRS LLC', url: SITE_URL },
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Verified Credentials', item: `${SITE_URL}/proof` },
    ],
  },
  mainEntity: {
    '@type': 'Organization',
    name: 'LONE TRS LLC',
    legalName: 'LONE TRS LLC',
    url: SITE_URL,
    hasCredential: [
      {
        '@type': 'EducationalOccupationalCredential',
        name: 'FMCSA DOT Number',
        credentialCategory: 'Federal Motor Carrier Safety Administration',
        identifier: '4476843',
      },
      {
        '@type': 'EducationalOccupationalCredential',
        name: 'FMCSA MC Number',
        credentialCategory: 'Motor Carrier Authority',
        identifier: '1767623',
      },
      {
        '@type': 'EducationalOccupationalCredential',
        name: 'Nevada Business License',
        credentialCategory: 'State Business License',
        identifier: 'NV20253377515',
      },
    ],
  },
};

export default function ProofPage() {
  return (
    <div className="min-h-screen bg-dark-900">
      <title>Verified Credentials &amp; DOT License | LONE TRS LLC Las Vegas</title>
      <meta
        name="description"
        content="LONE TRS LLC is fully licensed &amp; insured. DOT #4476843 · MC #1767623 · Nevada Business License NV20253377515. Certified transportation &amp; relocation company serving Las Vegas &amp; the West Coast."
      />
      <meta
        name="keywords"
        content="DOT licensed moving company Las Vegas, MC certified freight carrier Nevada, insured relocation company, transportation license Las Vegas, verified moving company credentials"
      />
      <link rel="canonical" href={`${SITE_URL}/proof`} />
      <meta property="og:title" content="Verified Credentials | LONE TRS LLC" />
      <meta
        property="og:description"
        content="Fully licensed, insured, and DOT/MC certified transportation company in Las Vegas. DOT #4476843 · MC #1767623."
      />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={`${SITE_URL}/proof`} />
      <meta name="twitter:card" content="summary_large_image" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(proofSchema) }}
      />

      <Navigation />
      
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden pt-32">
        <div className="absolute inset-0 bg-gradient-to-br from-dark-900 via-dark-800 to-dark-900">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-20 left-20 w-96 h-96 bg-electric-blue/20 rounded-full blur-3xl animate-pulse-glow" />
            <div className="absolute bottom-20 right-20 w-96 h-96 bg-electric-cyan/20 rounded-full blur-3xl animate-pulse-glow" style={{ animationDelay: '1s' }} />
          </div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="font-orbitron text-6xl md:text-7xl font-bold text-white mb-6 tracking-wider">
              VERIFIED <span className="text-electric-blue">CREDENTIALS</span>
            </h1>
            <p className="font-inter text-white/70 text-xl leading-relaxed">
              Fully licensed, insured, and certified for your complete peace of mind
            </p>
          </motion.div>
        </div>
      </section>

      <CredentialsHero />
      <CertificationTimeline />
      <TrustBadges />
      <CoverageMap />
      <Footer />
    </div>
  );
}
